import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Camera } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";

export default function AdminExercises() {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [assignFor, setAssignFor] = useState(null); // exercise
  const [selectedStudentIds, setSelectedStudentIds] = useState({});
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [ex, st] = await Promise.all([
        api.get("/admin/exercises"),
        api.get("/admin/students"),
      ]);
      setExercises(ex.data || []);
      setStudents((st.data || []).filter((s) => s.is_active));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!assignFor) return;
    const onKey = (e) => { if (e.key === "Escape") setAssignFor(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [assignFor]);

  const remove = async (id) => {
    if (!window.confirm("Delete this exercise? This also removes assignments.")) return;
    try {
      await api.delete(`/admin/exercises/${id}`);
      toast.success("Exercise deleted");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const openAssign = async (ex) => {
    setAssignFor(ex);
    try {
      const { data } = await api.get(`/admin/exercises/${ex.id}/assignments`);
      const map = {};
      data.forEach((a) => { map[a.student_id] = true; });
      setSelectedStudentIds(map);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const saveAssignments = async () => {
    if (!assignFor) return;
    const student_ids = Object.entries(selectedStudentIds).filter(([, v]) => v).map(([k]) => k);
    try {
      const { data } = await api.post("/admin/assignments", { exercise_id: assignFor.id, student_ids });
      toast.success(`Assigned to ${data.created} new student(s)`);
      setAssignFor(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <AppLayout variant="admin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Exercises</h1>
            <p className="text-stone mt-1">Create exercises and assign them to students.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="new-from-photo-button"
              onClick={() => navigate("/admin/exercises/new-from-photo")}
              className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-ink text-white font-bold btn-tactile hover:brightness-110"
            >
              <Camera className="w-5 h-5" /> From photo
            </button>
            <button
              data-testid="new-exercise-button"
              onClick={() => navigate("/admin/exercises/new")}
              className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover"
            >
              <Plus className="w-5 h-5" /> New Exercise
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-stone">Loading…</div>
        ) : exercises.length === 0 ? (
          <div className="p-8 bg-white rounded-3xl card-tactile text-center text-stone">
            No exercises yet — create your first one.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {exercises.map((e) => (
              <div key={e.id} data-testid={`exercise-card-${e.id}`} className="p-6 bg-white rounded-3xl card-tactile flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <div className="inline-flex items-center gap-2 flex-wrap">
                    <span className="px-3 py-1 rounded-full bg-sky-light text-sky-hover text-xs font-bold">L{e.level}</span>
                    <span className="px-3 py-1 rounded-full bg-canvas text-stone text-xs font-bold">W{e.week}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${e.is_published ? "bg-leaf/15 text-leaf" : "bg-lemon/40 text-ink"}`}>
                      {e.is_published ? "Published" : "Draft"}
                    </span>
                  </div>
                </div>
                <h3 className="font-heading text-xl font-bold text-ink mb-1">{e.title}</h3>
                <p className="text-sm text-stone mb-4">{e.topic}</p>
                <div className="text-sm text-stone mb-5">{e.question_count} question{e.question_count === 1 ? "" : "s"}</div>
                <div className="mt-auto flex flex-wrap gap-2">
                  <Link
                    data-testid={`edit-exercise-${e.id}`}
                    to={`/admin/exercises/${e.id}`}
                    className="inline-flex items-center gap-1 h-10 px-3 rounded-full bg-white border-2 border-cloud text-ink font-bold text-sm btn-tactile hover:border-sky"
                  ><Pencil className="w-4 h-4" /> Edit</Link>
                  <button
                    data-testid={`assign-exercise-${e.id}`}
                    onClick={() => openAssign(e)}
                    disabled={!e.is_published}
                    className="inline-flex items-center gap-1 h-10 px-3 rounded-full bg-sky text-white font-bold text-sm btn-tactile hover:bg-sky-hover disabled:opacity-40"
                  ><Users className="w-4 h-4" /> Assign</button>
                  <button
                    data-testid={`delete-exercise-${e.id}`}
                    onClick={() => remove(e.id)}
                    className="inline-flex items-center gap-1 h-10 px-3 rounded-full bg-watermelon-light text-watermelon font-bold text-sm btn-tactile hover:bg-watermelon hover:text-white"
                  ><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Assignment modal */}
        {assignFor && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setAssignFor(null)}>
            <div className="bg-white rounded-3xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6 card-tactile" onClick={(ev) => ev.stopPropagation()}>
              <h3 className="font-heading text-2xl font-bold text-ink mb-1">Assign exercise</h3>
              <p className="text-sm text-stone mb-4">{assignFor.title}</p>
              <div className="space-y-2 mb-6" data-testid="assign-student-list">
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-3 p-3 rounded-2xl bg-canvas cursor-pointer">
                    <input
                      data-testid={`assign-student-${s.id}`}
                      type="checkbox"
                      checked={!!selectedStudentIds[s.id]}
                      onChange={(ev) => setSelectedStudentIds((prev) => ({ ...prev, [s.id]: ev.target.checked }))}
                      className="w-5 h-5 accent-watermelon"
                    />
                    <div>
                      <div className="font-semibold text-ink">{s.name}</div>
                      <div className="text-xs text-stone">{s.email}</div>
                    </div>
                  </label>
                ))}
                {students.length === 0 && <div className="text-stone">No active students.</div>}
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setAssignFor(null)} className="h-11 px-5 rounded-full bg-white border-2 border-cloud text-ink font-bold btn-tactile">Cancel</button>
                <button data-testid="save-assignments-button" onClick={saveAssignments} className="h-11 px-6 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover">Save assignments</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
